import React, { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLocation } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const SmoothScroll = ({ children }) => {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);
  const isAdminPath = pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPath) {
      return;
    }

    // Bypass Lenis smooth scrolling on mobile / touch devices for native high-FPS scrolling
    const isTouchDevice =
      typeof window !== 'undefined' &&
      (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024);

    if (isTouchDevice) {
      return;
    }

    // Initialize Lenis with snappy performance settings
    const lenis = new Lenis({
      duration: 0.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.0,
      infinite: false,
      prevent: (node) => {
        return node.closest('[data-lenis-prevent]');
      },
    });

    lenisRef.current = lenis;
    window.__lenis = lenis;

    // Sync ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    const gsapTicker = (time) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(gsapTicker);

    // Observer to stop Lenis when a modal is open (body overflow hidden)
    const observer = new MutationObserver(() => {
      const isLocked = document.body.style.overflow === 'hidden';
      if (isLocked) {
        lenis.stop();
      } else {
        lenis.start();
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });

    return () => {
      lenis.destroy();
      window.__lenis = null;
      gsap.ticker.remove(gsapTicker);
      ScrollTrigger.getAll().forEach(t => t.kill());
      observer.disconnect();
    };

  }, [isAdminPath]);

  // Handle scroll to top or restore on path change
  useEffect(() => {
    if (isAdminPath) {
      window.scrollTo(0, 0);
      return;
    }

    const savedPos = sessionStorage.getItem(`cg_scroll_${pathname}`);
    if (savedPos && (pathname === '/all-games' || pathname === '/platforms' || pathname === '/blog')) {
      const targetY = parseFloat(savedPos) || 0;
      if (targetY > 0) {
        if (lenisRef.current) {
          lenisRef.current.scrollTo(targetY, { immediate: true, force: true });
        }
        window.scrollTo({ top: targetY, behavior: 'instant' });
        return;
      }
    }

    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true, force: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, isAdminPath]);

  return <>{children}</>;
};

export default SmoothScroll;
